/*
 * Handles authentication data in REST blocks being saved to My Libraries.
 * Direct username/password values and literal Authorization header rows are removed,
 * while credentials supplied by functional blocks and dynamic header keys are preserved.
 */
// Match only the two REST block families, with optional version suffixes such as _V2 or _V3.
const REST_BLOCK_TYPE_PATTERN = /^(?:rest_service|load_data_table_rest)(?:_V\d+)?$/;
const LITERAL_CREDENTIAL_BLOCK_TYPES = new Set(['fixed_width_text', 'password_text', 'text']);

// Main entry points

// Checks whether saving will remove direct REST credentials or Authorization headers.
export function hasRestAuthenticationDetails(xml) {
    return getRestBlocks(xml).some(restBlock =>
        hasDirectlyStoredCredentials(restBlock) || hasLiteralAuthorizationHeader(restBlock)
    );
}

// Removes direct REST credentials and Authorization headers from the XML being saved to My Libraries.
export function sanitizeRestBlocksForLibrary(xml) {
    getRestBlocks(xml).forEach(restBlock => {
        clearDirectCredentials(restBlock);
        removeAuthorizationHeaders(restBlock);
    });

    return xml;
}

// Basic XML helpers

const getDirectChildren = (element, tagName) => Array.from(element?.children || [])
    .filter(child => child.tagName?.toLowerCase() === tagName);

const getDirectChild = (element, tagName, name) => getDirectChildren(element, tagName)
    .find(child => name === undefined || child.getAttribute('name') === name);

const getConnectedBlock = valueElement => {
    // When a shadow is overridden, Blockly XML contains both elements and the regular block is active.
    return getDirectChild(valueElement, 'block') || getDirectChild(valueElement, 'shadow');
};

// REST block helpers

const isRestBlock = blockElement => {
    const type = blockElement.getAttribute('type') || '';
    return REST_BLOCK_TYPE_PATTERN.test(type);
};

const getRestBlocks = xml => {
    if (!xml) return [];

    // The saved group may contain REST blocks at any nesting level or block version.
    const blocks = [];
    if (xml.tagName?.toLowerCase() === 'block') {
        blocks.push(xml);
    }
    blocks.push(...Array.from(xml.getElementsByTagName?.('block') || []));
    return blocks.filter(isRestBlock);
};

// Direct username/password helpers

const isLiteralCredentialBlock = blockElement => blockElement &&
    LITERAL_CREDENTIAL_BLOCK_TYPES.has(blockElement.getAttribute('type'));

const getLiteralBlockValue = blockElement => Array.from(blockElement?.getElementsByTagName('field') || [])
    .map(field => field.textContent || '')
    .join('');

const getCredentialBlock = (restBlock, inputName) => {
    const valueElement = getDirectChild(restBlock, 'value', inputName);
    return getConnectedBlock(valueElement);
};

// Detect only directly entered values. Functional blocks such as variables or secret-key lookups are preserved.
const hasDirectlyStoredCredentials = restBlock =>
    ['USERNAME', 'PASSWORD'].some(inputName => {
        const credentialBlock = getCredentialBlock(restBlock, inputName);
        return isLiteralCredentialBlock(credentialBlock) && getLiteralBlockValue(credentialBlock).length > 0;
    });

const replaceWithEmptyCredentialShadow = (restBlock, inputName, shadowType) => {
    const valueElement = getDirectChild(restBlock, 'value', inputName);
    const credentialBlock = getConnectedBlock(valueElement);
    if (!valueElement || !isLiteralCredentialBlock(credentialBlock)) return false;

    // Keep the input valid by replacing its stored value with the block's normal empty input.
    while (valueElement.firstChild) {
        valueElement.firstChild.remove();
    }

    const shadow = valueElement.ownerDocument.createElement('shadow');
    shadow.setAttribute('type', shadowType);
    const mutation = valueElement.ownerDocument.createElement('mutation');
    mutation.setAttribute('length', '29');
    shadow.appendChild(mutation);
    valueElement.appendChild(shadow);
    return true;
};

const clearDirectCredentials = restBlock => {
    const usernameCleared = replaceWithEmptyCredentialShadow(restBlock, 'USERNAME', 'fixed_width_text');
    const passwordCleared = replaceWithEmptyCredentialShadow(restBlock, 'PASSWORD', 'password_text');
    if (!usernameCleared && !passwordCleared) return;

    // Reset the saved state so the empty username/password inputs display correctly when reopened.
    const mutation = getDirectChild(restBlock, 'mutation');
    if (mutation) {
        if (usernameCleared) mutation.setAttribute('username', 'false');
        if (passwordCleared) mutation.setAttribute('password', 'false');
        mutation.setAttribute('showcancelupdatebutton', 'false');
    }
};

// Authorization header helpers

// Matches rest_params and versioned names such as rest_params_v2 without maintaining a list.
const isRestParamsBlock = blockElement => blockElement &&
    /^rest_params(?:_v\d+)?$/.test(blockElement.getAttribute('type') || '');

const isAuthorizationHeader = keyElement => {
    const keyBlock = getConnectedBlock(keyElement);
    if (!isLiteralCredentialBlock(keyBlock)) return false;
    return getLiteralBlockValue(keyBlock).trim().toLowerCase() === 'authorization';
};

const hasLiteralAuthorizationHeader = restBlock => {
    const headersValue = getDirectChild(restBlock, 'value', 'HEADERS');
    const headersBlock = getConnectedBlock(headersValue);
    if (!isRestParamsBlock(headersBlock)) return false;

    // A dynamically generated key cannot be identified safely at save time, so only literal keys are checked.
    return getDirectChildren(headersBlock, 'value').some(element =>
        /^key_\d+$/.test(element.getAttribute('name') || '') && isAuthorizationHeader(element)
    );
};

const removeAuthorizationHeaders = restBlock => {
    const headersValue = getDirectChild(restBlock, 'value', 'HEADERS');
    const headersBlock = getConnectedBlock(headersValue);
    if (!isRestParamsBlock(headersBlock)) return;

    // Header keys and values use matching numeric indexes; both parts of an Authorization row must be removed.
    const keyElements = getDirectChildren(headersBlock, 'value')
        .map(element => ({
            element,
            index: Number(element.getAttribute('name')?.match(/^key_(\d+)$/)?.[1]),
        }))
        .filter(({ index }) => Number.isInteger(index))
        .sort((left, right) => left.index - right.index);

    let nextIndex = 0;
    let removedAuthorizationHeader = false;
    keyElements.forEach(({ element: keyElement, index }) => {
        const valueElement = getDirectChild(headersBlock, 'value', `value_${index}`);
        if (isAuthorizationHeader(keyElement)) {
            headersBlock.removeChild(keyElement);
            if (valueElement) headersBlock.removeChild(valueElement);
            removedAuthorizationHeader = true;
            return;
        }

        keyElement.setAttribute('name', `key_${nextIndex}`);
        if (valueElement) valueElement.setAttribute('name', `value_${nextIndex}`);
        nextIndex++;
    });

    if (removedAuthorizationHeader) {
        // Update the saved row count so the remaining headers display correctly when reopened.
        let mutation = getDirectChild(headersBlock, 'mutation');
        if (!mutation) {
            mutation = headersBlock.ownerDocument.createElement('mutation');
            headersBlock.insertBefore(mutation, headersBlock.firstChild);
        }
        mutation.setAttribute('length', String(nextIndex));
    }
};
